// @flow
import * as React from 'react';
import { Button, ButtonProps, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';
import { ModalCloseButton } from '@coverright/ui/shared';
import { useContext, useRef, useState } from 'react';
import { NextActionForm } from './next-action-form';
import { ActionItemsByClientIdDocument, useDeleteActionItemMutation } from '../../api';
import { useConfirm } from '@coverright/features';
import { theme } from '../../../app/theme';
import { ActionItemInput } from '@coverright/data-access/types/enrollment';

type InitialValuesType = ActionItemInput & {clientName?: string | null}

interface Props {
  open: (initialValues?: InitialValuesType, callbackFunc?: () => void) => void;
}

const NextActionFormContext = React.createContext<Props>({
  open: () => {}
})


export function withNextActionForm<P>(WrappedComponent: React.FC<P>) {
  return (props: P) => {
    const [open, setOpen] = useState(false);
    const [initialValues, setInitialValues] = useState<InitialValuesType>();
    const callback = useRef<() => void>();

    const confirm = useConfirm(theme);
    const [deleteAction] = useDeleteActionItemMutation({
      refetchQueries: [{
        query: ActionItemsByClientIdDocument,
        variables: { clientId: initialValues?.clientId }
      }],
      onCompleted: data => callback.current && callback.current()
    })

    const handleDeleteClick = () => {
      confirm({
        content: 'Are you sure you want to delete this action?',
        okButtonTitle: 'Yes',
        cancelButtonTitle: 'No',
        title: 'Attention!',
      })
        .then(val => {
          if (val) {
            setOpen(false)
            deleteAction({variables: {id: initialValues?.id} })
          }
        })
    }

    const openModal = (initial?: InitialValuesType, callbackFunc?: () => void) => {
      setInitialValues(initial);
      callback.current = callbackFunc;
      setOpen(true)
    }

    return <NextActionFormContext.Provider value={{open: openModal}}>
      <Dialog fullWidth maxWidth={'sm'} open={open} onClose={() => setOpen(false)}>
        <DialogTitle variant={'h3'}>
          Next action

          <Stack gap={2} direction={'row'} alignItems={'center'} sx={{position: 'absolute', top: 4, right: 8}}>
            {initialValues?.id && <Button onClick={handleDeleteClick} size={'small'} variant={'outlined'} color={'error'}>
              Delete
            </Button>}
            <ModalCloseButton onClick={() => setOpen(false)} />
          </Stack>
        </DialogTitle>
        <DialogContent>
          <NextActionForm initialValues={initialValues} onSubmit={() => {
            setOpen(false);
            callback.current && callback.current();
          }} />
        </DialogContent>
      </Dialog>
      <WrappedComponent {...props} />
    </NextActionFormContext.Provider>
  }
}

export const useNextActionForm = () => useContext(NextActionFormContext)

export const NextActionFormButton = withNextActionForm((props: ButtonProps & {initialValues?: InitialValuesType, onSubmit?: () => void}) => {
  const {initialValues, onSubmit, ...rest} = props;
  const {open} = useNextActionForm()

  return (<Button size={'small'} {...rest} onClick={() => open(initialValues, onSubmit)}>
    {rest.children || 'Add action'}
  </Button>);
})
